/**
 * Clearing the interview feature's browser storage (sittingStorage and
 * writtenStorage), the interview counterpart of paperProgressCache's
 * clearPaperProgressCaches and purgeLegacyPaperProgressCaches. AuthContext
 * calls both at the same places: logout, the end of a session and a sign-in as
 * another account clear every user's sittings, drafts and results, because a
 * shared school computer must not keep one student's interviews for the next.
 *
 * Before the user id went into the keys, every account on the browser shared
 * `<prefix><paperId>:<sessionId>`. Those legacy keys are deleted on first load
 * (purgeLegacyInterviewProgress) and never read.
 */
import { SITTING_PREFIX } from './sittingStorage';
import { DRAFT_PREFIX, RESULT_PREFIX } from './writtenStorage';
import { USER_SEPARATOR } from '../quiz-generation/utils/paperProgressCache';

const INTERVIEW_PREFIXES = [SITTING_PREFIX, DRAFT_PREFIX, RESULT_PREFIX];

/** sittingReport's cache of a finished paper's marked answers and report, in
 * this tab's sessionStorage, keyed by run ids alone. A hit is served without
 * asking the server, so it goes whenever the interview progress goes. */
export const REPORT_PREFIX = 'ivori-interview-report:';
const isReportKey = (key) => key.startsWith(REPORT_PREFIX);

const isInterviewKey = (key) => INTERVIEW_PREFIXES.some((prefix) => key.startsWith(prefix));
const isLegacyKey = (key) => isInterviewKey(key) && !key.includes(USER_SEPARATOR);

function removeMatching(storage, predicate) {
  const keys = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key != null && predicate(key)) keys.push(key);
  }
  keys.forEach((key) => storage.removeItem(key));
  return keys.length;
}

// Both run on paths that must finish (app start, logout and session
// expiry), so a storage error is logged and 0 is returned.

/** Delete the unkeyed interview keys every account on the browser shared. */
export function purgeLegacyInterviewProgress(storage = localStorage) {
  try {
    return removeMatching(storage, isLegacyKey);
  } catch (err) {
    console.error('legacy interview progress purge failed', err?.name || null);
    return 0;
  }
}

/** Delete every interview key, legacy and per-user, for every user, and
 * every cached report in this tab. */
export function clearInterviewProgress(storage = localStorage, session = sessionStorage) {
  try {
    removeMatching(session, isReportKey);
  } catch (err) {
    console.error('interview report cache clear failed', err?.name || null);
  }
  try {
    return removeMatching(storage, isInterviewKey);
  } catch (err) {
    console.error('interview progress clear failed', err?.name || null);
    return 0;
  }
}
